import React, { useMemo } from 'react';
import { Button, Spin, Table } from 'antd';
import moment from 'moment';
import {
	WrenchModeEvent,
	formatDuration,
	countCompletedWorkOrders,
	parseLocationEvents,
	collectScheduleEvents,
	filterEventsByLocation,
} from '../../utils/WrenchModeEventUtils';
import { workOrderSearchAndAPIAdaptor } from '../../thunks/work_orders_thunks_wrapper';

interface FlattenedRow {
	key: string;
	scheduleId: number;
	date: string;
	locationName: string;
	locationId: number;
	workOrdersCompleted: number;
	workOrdersTotal: number;
	driveTimeMs: number | null;
	workTimeMs: number | null;
}

interface WrenchModeSchedulesTableProps {
	schedules: any[];
	loading: boolean;
	loadingMore: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
	highlightedDate: string | null;
	onRowClick: (date: string, locationId: number) => void;
	locationIds: number[] | undefined;
}

const computeRowSpanMap = (rowList: FlattenedRow[]): Record<string, number> => {
	const spanMap: Record<string, number> = {};

	for (let groupStart = 0; groupStart < rowList.length; ) {
		const currentDate = rowList[groupStart].date;
		let groupEnd = groupStart + 1;

		while (groupEnd < rowList.length && rowList[groupEnd].date === currentDate) {
			groupEnd++;
		}

		spanMap[rowList[groupStart].key] = groupEnd - groupStart;

		for (let inner = groupStart + 1; inner < groupEnd; inner++) {
			spanMap[rowList[inner].key] = 0;
		}

		groupStart = groupEnd;
	}

	return spanMap;
};

const WrenchModeSchedulesTable: React.FC<WrenchModeSchedulesTableProps> = ({
	schedules,
	loading,
	loadingMore,
	hasMore,
	onLoadMore,
	highlightedDate,
	onRowClick,
	locationIds,
}) => {
	const { rows, rowSpanMap } = useMemo(() => {
		const hasLocationFilter = Boolean(locationIds && locationIds.length);
		const flatRows: FlattenedRow[] = [];

		for (const schedule of schedules) {
			const locations = schedule.locations || [];

			const allEvents = collectScheduleEvents(schedule);
			if (allEvents.length === 0) continue;

			if (locations.length === 0) {
				if (!hasLocationFilter) {
					flatRows.push({
						key: schedule.id + '-0',
						scheduleId: schedule.id,
						date: schedule.date,
						locationName: 'No location',
						locationId: 0,
						workOrdersCompleted: 0,
						workOrdersTotal: 0,
						driveTimeMs: null,
						workTimeMs: null,
					});
				}
			} else {
				for (let locIndex = 0; locIndex < locations.length; locIndex++) {
					const location = locations[locIndex];

					if (hasLocationFilter && locationIds.indexOf(location.locationId) === -1) continue;

					const locationName = (location.location && location.location.name) || 'Unknown Location';
					const workOrderIds = location.workOrderIds || [];

					const locationEvents = location.events || [];
					let driveTimeMs: number | null = null;
					let workTimeMs: number | null = null;

					if (locationEvents.length > 0) {
						const parsed = parseLocationEvents(locationEvents);
						driveTimeMs = parsed.driveTimeMs;
						workTimeMs = parsed.totalWorkMs;
					}

					flatRows.push({
						key: schedule.id + '-' + locIndex,
						scheduleId: schedule.id,
						date: schedule.date,
						locationName,
						locationId: location.locationId,
						workOrdersCompleted: countCompletedWorkOrders(
							workOrderSearchAndAPIAdaptor(
								(location.workOrders || []).map((workOrder) => ({ source: workOrder }))
							),
							schedule.supplierEmail,
							schedule.date
						),
						workOrdersTotal: workOrderIds.length,
						driveTimeMs,
						workTimeMs,
					});
				}
			}
		}

		return { rows: flatRows, rowSpanMap: computeRowSpanMap(flatRows) };
	}, [schedules, locationIds]);

	const hasLocationFilter = Boolean(locationIds && locationIds.length);
	const emptyText = hasLocationFilter
		? 'No logs match the selected locations this month'
		: 'No logs for this month';

	const columns = [
		{
			title: 'Date',
			dataIndex: 'date',
			key: 'date',
			render: (date: string, record: FlattenedRow) => ({
				children: moment(date).format('ddd, MMM D'),
				props: { rowSpan: rowSpanMap[record.key] },
			}),
		},
		{
			title: 'Location',
			dataIndex: 'locationName',
			key: 'locationName',
		},
		{
			title: 'Work Orders',
			key: 'workOrders',
			render: (_: any, record: FlattenedRow) =>
				record.workOrdersCompleted + '/' + record.workOrdersTotal,
		},
		{
			title: 'Drive Time',
			key: 'driveTime',
			render: (_: any, record: FlattenedRow) => {
				if (record.driveTimeMs === null) return '-';
				return formatDuration(record.driveTimeMs);
			},
		},
		{
			title: 'Work Time',
			key: 'workTime',
			render: (_: any, record: FlattenedRow) => {
				if (record.workTimeMs === null) return '-';
				return formatDuration(record.workTimeMs);
			},
		},
	];

	const renderFooter =
		hasMore || loadingMore
			? () => (
					<div className="flex justify-center">
						{loadingMore ? (
							<Spin />
						) : (
							<Button onClick={onLoadMore} size="small">
								Load More
							</Button>
						)}
					</div>
			  )
			: undefined;

	return (
		<Table
			className="wrenchModeTable"
			columns={columns}
			dataSource={rows}
			pagination={false}
			loading={loading}
			rowClassName={(record: FlattenedRow) => {
				if (highlightedDate && record.date === highlightedDate) {
					return 'wrench-mode-row-highlighted';
				}
				return '';
			}}
			onRow={(record: FlattenedRow) => ({
				onClick: () => onRowClick(record.date, record.locationId),
				style: { cursor: 'pointer' },
			})}
			size="middle"
			locale={{ emptyText: loading ? ' ' : emptyText }}
			footer={renderFooter}
		/>
	);
};

export default WrenchModeSchedulesTable;
